import React, { useMemo, useState, useEffect } from 'react';
import { Printer, History, AlertCircle, RotateCcw, X, CheckSquare, Square, PackageCheck } from 'lucide-react';
import { CompanyFilterSidebar } from '../components/CompanyFilterSidebar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { SearchBar } from '../components/ui/SearchBar';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useOrders } from '../contexts/OrderContext';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { useNotifications } from '../contexts/NotificationContext';
import { cleanProductName } from '../utils/formatters';
export function LabelControl() {
  // NOTA: Asegúrate de que tu OrderContext exporte la función 'refreshOrders'
  const {
    orders,
    updateLabelStatus,
    updateOrderStatus,
    refreshOrders
  } = useOrders();
  const {
    addLog
  } = useActivityLog();
  const {
    addNotification
  } = useNotifications();
  const {
    showToast
  } = useToast();

  // Estados
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState('Todo');
  const [branchFilter, setBranchFilter] = useState('Todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());
  const [showTotalLabelsModal, setShowTotalLabelsModal] = useState(false);
  const [showDeliveryConfirmModal, setShowDeliveryConfirmModal] = useState(false);
  const [orderToDeliver, setOrderToDeliver] = useState<any>(null);

  // --- EFECTO PARA ACTUALIZACIÓN EN TIEMPO REAL (POLLING) ---
  useEffect(() => {
    // Intentar actualizar las órdenes cada 5 segundos
    if (refreshOrders) {
      const interval = setInterval(() => {
        refreshOrders();
      }, 5000);

      // Limpiar el intervalo al desmontar el componente
      return () => clearInterval(interval);
    }
  }, [refreshOrders]);

  // Métricas rápidas
  const pendingLabels = orders.filter(o => o.labelStatus === 'Pendiente' && o.status !== 'Entregado' && o.status !== 'Cancelado').length;
  const generatedLabels = orders.filter(o => o.labelStatus === 'Generado' && o.status !== 'Entregado' && o.status !== 'Cancelado').length;
  const totalActiveLabels = orders.filter(o => o.status !== 'Cancelado' && o.status !== 'Entregado').length;
  const metrics = [{
    label: 'Total de etiquetas',
    value: totalActiveLabels.toString(),
    onClick: () => setShowTotalLabelsModal(true),
    clickable: true
  }, {
    label: 'Etiquetas Generadas',
    value: generatedLabels.toString(),
    clickable: false
  }, {
    label: 'Etiquetas Pendientes',
    value: pendingLabels.toString(),
    clickable: false
  }];

  // --- CALCULAR SUCURSALES DISPONIBLES (Dinámico) ---
  const companyBranches = useMemo(() => {
    if (!selectedCompany) return ['Todas'];

    // Obtenemos las sucursales únicas de las órdenes de esta empresa
    const branches = new Set(orders.filter(o => o.company === selectedCompany).map(o => o.branch).filter(Boolean) // Filtrar nulos o vacíos
    );

    // Convertimos a array y ordenamos alfabéticamente
    return ['Todas', ...Array.from(branches).sort()];
  }, [orders, selectedCompany]);

  // --- FILTRADO PRINCIPAL ---
  const ordersToDisplay = useMemo(() => {
    const filtered = orders.filter(o => {
      // 1. Filtros básicos de estado
      if (o.status === 'Entregado' || o.status === 'Cancelado') return false;

      // 2. Filtro por Empresa
      if (selectedCompany && o.company !== selectedCompany) return false;

      // 3. Filtro Historial vs Pendiente
      if (showHistory) {
        if (o.labelStatus !== 'Generado') return false;
      } else {
        if (o.labelStatus !== 'Pendiente') return false;
      }

      // 4. Filtro por Sucursal (NUEVO)
      if (branchFilter !== 'Todas' && o.branch !== branchFilter) return false;

      // 5. Filtro por Tipo (Almuerzo/Cena)
      if (typeFilter !== 'Todo' && o.catalogType !== typeFilter) return false;

      // 6. Buscador
      if (searchTerm && !o.name.toLowerCase().includes(searchTerm.toLowerCase()) && !o.orderNo.includes(searchTerm) && !o.product.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      return true;
    });

    // Expandir órdenes según cantidad (para impresión individual de etiquetas)
    return filtered.flatMap(order => {
      const quantity = order.quantity || 1;
      return Array(quantity).fill(null).map((_, index) => ({
        ...order,
        id: `${order.id}-${index}`,
        originalId: order.id,
        quantity: 1,
        // Forzamos a 1 para visualización individual
        originalQuantity: order.quantity
      }));
    });
  }, [orders, selectedCompany, showHistory, typeFilter, branchFilter, searchTerm]);

  // Resetear filtros al cambiar de empresa
  const handleCompanySelect = (company: string) => {
    setSelectedCompany(company);
    setSelectedOrderIds(new Set());
    setBranchFilter('Todas'); // Resetear sucursal al cambiar empresa
  };
  const toggleOrderSelection = (orderId: string) => {
    const newSelected = new Set(selectedOrderIds);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrderIds(newSelected);
  };
  const toggleSelectAll = () => {
    if (selectedOrderIds.size === ordersToDisplay.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(ordersToDisplay.map(o => o.id)));
    }
  };

  // --- IMPRESIÓN DE ETIQUETAS (AVERY 5161) ---
  const handlePrint = (ordersToPrint: any[], markAsGenerated: boolean = true) => {
    if (!selectedCompany) {
      showToast('warning', 'Empresa requerida', 'Debes seleccionar una empresa');
      return;
    }
    let finalOrdersToPrint = ordersToPrint;
    if (selectedOrderIds.size > 0) {
      finalOrdersToPrint = ordersToPrint.filter(o => selectedOrderIds.has(o.id));
    }
    if (finalOrdersToPrint.length === 0) {
      showToast('warning', 'Sin selección', 'No hay etiquetas para imprimir');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const chunkSize = 20;
    const pages = [];
    for (let i = 0; i < finalOrdersToPrint.length; i += chunkSize) {
      pages.push(finalOrdersToPrint.slice(i, i + chunkSize));
    }
    const pagesHtml = pages.map(pageOrders => {
      const labelsHtml = pageOrders.map(order => `<div class="label">
                <div class="line1">${order.company} — 8097138527</div>
                <div class="line2"><strong>${order.branch} — Envío</strong></div>
                <div class="line3"><strong>${order.name}</strong></div>
                <div class="line4">${cleanProductName(order.product)}</div>
              </div>`).join('');
      return `<div class="page">${labelsHtml}</div>`;
    }).join('');
    const cssStyles = `
      @page { size: letter; margin: 0; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; background: white; }
      .page {
        width: 8.5in; height: 11in;
        padding-top: 0.5in;
        padding-left: 0.156in;
        padding-right: 0.156in;
        display: grid;
        grid-template-columns: 4in 4in;
        grid-template-rows: repeat(10, 1in);
        column-gap: 0.188in;
        row-gap: 0;
        page-break-after: always;
      }
      .page:last-child { page-break-after: auto; }
      .label {
        width: 100%; height: 100%;
        display: flex; flex-direction: column;
        justify-content: center; align-items: center;
        text-align: center; overflow: hidden;
        padding: 0.05in 0.1in;
      }
      .line1 { font-size: 8pt; color: #000; margin-bottom: 2px; }
      .line2 { font-size: 9pt; color: #000; margin-bottom: 2px; text-transform: uppercase; }
      .line3 { font-size: 10pt; color: #000; margin-bottom: 2px; font-weight: bold; }
      .line4 { font-size: 9pt; color: #000; line-height: 1.1; max-height: 2.2em; overflow: hidden; }
      @media print { body { -webkit-print-color-adjust: exact; } }
    `;
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Etiquetas Avery 5161</title>
        <style>${cssStyles}</style>
      </head>
      <body>
        ${pagesHtml}
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 500);
            window.onafterprint = function() { window.close(); };
          };
        </script>
      </body>
      </html>`;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    if (markAsGenerated) {
      const uniqueOriginalIds = Array.from(new Set(finalOrdersToPrint.map(o => o.originalId || o.id)));
      updateLabelStatus(uniqueOriginalIds, 'Generado');
      addLog('Etiquetas Impresas', `${finalOrdersToPrint.length} etiquetas de ${selectedCompany}`, 'order');
      showToast('success', 'Etiquetas impresas', `${finalOrdersToPrint.length} etiquetas generadas`);
      setSelectedOrderIds(new Set());
    } else {
      addLog('Etiquetas Reimprimidas', `${finalOrdersToPrint.length} etiquetas de ${selectedCompany}`, 'order');
      showToast('info', 'Etiquetas reimprimidas', `${finalOrdersToPrint.length} etiquetas reimpresas`);
    }
  };

  // --- IMPRESIÓN CONDUCE DE ENTREGA (Filtrado por Sucursal) ---
  const handlePrintDeliveryNote = () => {
    if (!selectedCompany) {
      showToast('warning', 'Empresa requerida', 'Selecciona una empresa');
      return;
    }
    let ordersToPrint = ordersToDisplay;
    if (selectedOrderIds.size > 0) {
      ordersToPrint = ordersToDisplay.filter(o => selectedOrderIds.has(o.id));
    }
    if (ordersToPrint.length === 0) {
      showToast('warning', 'Sin órdenes', 'No hay órdenes para imprimir');
      return;
    }

    // Detectar nombre de sucursal para el encabezado
    const uniqueBranches = new Set(ordersToPrint.map(o => o.branch));
    const branchDisplay = uniqueBranches.size === 1 ? ordersToPrint[0].branch // Si solo hay una, mostramos su nombre
    : branchFilter !== 'Todas' ? branchFilter // Si filtramos por el dropdown, usamos ese nombre
    : 'Múltiples Sucursales'; // Si hay mezcla

    // Configuración Estilo
    const primaryColor = '#1f2937';
    const headerBgColor = '#1e293b';
    const accentColor = '#2563eb';
    const companyName = 'LA COCHERA';
    const companySubtitle = 'RESTAURANT & DELIVERY';
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const today = new Date().toLocaleDateString('es-DO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const time = new Date().toLocaleTimeString('es-DO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    const totalQuantity = ordersToPrint.reduce((sum, o) => sum + (o.quantity || 1), 0);
    const tableRows = ordersToPrint.map((order, index) => {
      return `
        <tr>
          <td style="text-align: center; font-weight: bold; color: #6b7280;">${index + 1}</td>
          <td style="font-family: monospace; font-size: 11px;">${order.orderNo}</td>
          <td><div class="row-main">${order.name}</div></td>
          <td><div class="row-main">${cleanProductName(order.product)}</div></td>
          <td><span class="badge">${order.branch || 'Principal'}</span></td>
          <td style="text-align: center; font-weight: bold;">${order.quantity || 1}</td>
        </tr>`;
    }).join('');
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Conduce - ${selectedCompany}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
          @page { size: letter landscape; margin: 0.25in; }
          body {
            font-family: 'Inter', sans-serif; margin: 0; padding: 20px;
            color: #111827; background: #fff; font-size: 12px;
            -webkit-print-color-adjust: exact; box-sizing: border-box; width: 100%;
          }
          .container { width: 100%; margin: 0 auto; }
          .header-strip { height: 6px; background: ${accentColor}; width: 100%; margin-bottom: 20px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
          .logo-area h1 { font-size: 28px; font-weight: 800; margin: 0; color: ${primaryColor}; text-transform: uppercase; line-height: 1; }
          .logo-area p { font-size: 10px; color: #6b7280; margin: 6px 0 0 0; letter-spacing: 2px; text-transform: uppercase; font-weight: 600; }
          .doc-meta { text-align: right; }
          .doc-title { font-size: 20px; font-weight: 800; color: ${primaryColor}; text-transform: uppercase; margin-bottom: 4px; }
          .doc-date { font-size: 11px; color: #4b5563; font-weight: 500; }
          .info-section { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-bottom: 25px; }
          .info-card { background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 16px; }
          .card-label { font-size: 9px; text-transform: uppercase; color: #6b7280; font-weight: 700; margin-bottom: 4px; letter-spacing: 0.5px; }
          .card-value { font-size: 13px; font-weight: 700; color: #000; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          table { width: 100%; border-collapse: separate; border-spacing: 0; margin-bottom: 20px; border-radius: 0; overflow: hidden; }
          thead { background-color: ${headerBgColor}; color: white; }
          th { padding: 10px 12px; text-align: left; font-weight: 700; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; }
          td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
          tbody tr:nth-child(even) { background-color: #f8fafc; }
          .row-main { font-weight: 600; color: #334155; font-size: 11px; }
          .badge { background: #eef2ff; color: #4338ca; padding: 4px 8px; border-radius: 4px; font-size: 9px; font-weight: 700; text-transform: uppercase; display: inline-block; }
          .footer-section { display: flex; justify-content: flex-end; margin-top: 10px; margin-bottom: 40px; }
          .total-box { background: ${headerBgColor}; color: white; padding: 10px 25px; border-radius: 4px; text-align: right; display: flex; gap: 20px; align-items: center; }
          .total-label { font-size: 10px; opacity: 0.9; text-transform: uppercase; font-weight: 600; }
          .total-value { font-size: 18px; font-weight: 800; }
          .signatures { margin-top: 50px; display: grid; grid-template-columns: 1fr 1fr; gap: 100px; padding: 0 40px; }
          .sig-line { border-top: 1px solid #94a3b8; padding-top: 8px; text-align: center; }
          .sig-title { font-weight: 700; font-size: 10px; text-transform: uppercase; color: #475569; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; } .container { width: 100%; max-width: none; } }
        </style>
      </head>
      <body>
        <div class="header-strip"></div>
        <div class="container">
          <div class="header">
            <div class="logo-area">
              <h1>${companyName}</h1>
              <p>${companySubtitle}</p>
            </div>
            <div class="doc-meta">
              <div class="doc-title">CONDUCE DE ENTREGA</div>
              <div class="doc-date">Fecha: ${today}</div>
              <div class="doc-date">Hora: ${time}</div>
            </div>
          </div>
          <div class="info-section">
            <div class="info-card">
              <div class="card-label">Empresa Destino</div>
              <div class="card-value">${selectedCompany}</div>
            </div>
            <div class="info-card">
              <div class="card-label">Sucursal / Área</div>
              <div class="card-value">${branchDisplay}</div>
            </div>
             <div class="info-card">
              <div class="card-label">Responsable Envío</div>
              <div class="card-value">Logística / Despacho</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 40px; text-align: center;">#</th>
                <th style="width: 100px;">CÓDIGO</th>
                <th>CLIENTE</th>
                <th>PRODUCTO</th>
                <th>SUCURSAL</th>
                <th style="width: 60px; text-align: center;">CANT.</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <div class="footer-section">
            <div class="total-box">
              <div class="total-label">Total Unidades</div>
              <div class="total-value">${totalQuantity}</div>
            </div>
          </div>
          <div class="signatures">
            <div class="sig-block"><div class="sig-line"><div class="sig-title">Entregado Por</div></div></div>
            <div class="sig-block"><div class="sig-line"><div class="sig-title">Recibido Por</div></div></div>
          </div>
        </div>
        <script>
          window.onload = function() { setTimeout(function() { window.print(); }, 250); window.onafterprint = function() { window.close(); }; };
        </script>
      </body>
      </html>`;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    addLog('Conduce Impreso', `${ordersToPrint.length} órdenes de ${selectedCompany}`, 'order');
    showToast('success', 'Conduce impreso', `Conduce generado para: ${branchDisplay}`);
  };
  const handlePrintPending = () => {
    const pendingOrders = ordersToDisplay.filter(o => o.labelStatus === 'Pendiente');
    handlePrint(pendingOrders, true);
  };
  const handleReprintGenerated = () => {
    const generatedOrders = ordersToDisplay.filter(o => o.labelStatus === 'Generado');
    handlePrint(generatedOrders, false);
  };
  const handleMarkAsDelivered = (order: any) => {
    setOrderToDeliver(order);
    setShowDeliveryConfirmModal(true);
  };
  const confirmDelivery = () => {
    if (orderToDeliver) {
      const idToUpdate = orderToDeliver.originalId || orderToDeliver.id;
      updateOrderStatus(idToUpdate, 'Entregado');
      addLog('Pedido Entregado', `Orden #${orderToDeliver.orderNo} - ${orderToDeliver.name}`, 'order');
      addNotification({
        type: 'order',
        title: 'Pedido Entregado',
        message: `Tu pedido #${orderToDeliver.orderNo} ha sido entregado`,
        orderId: idToUpdate
      });
      showToast('success', 'Pedido entregado', `Orden #${orderToDeliver.orderNo} marcada como entregada`);
      setShowDeliveryConfirmModal(false);
      setOrderToDeliver(null);
    }
  };
  return <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] bg-gray-50 dark:bg-gray-950 overflow-hidden" data-id="element-1750">
      {/* Sidebar - Selección de Empresa */}
      <div className="w-full lg:w-80 p-4 lg:p-6 lg:pr-0 overflow-y-auto border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900" data-id="element-1751">
        <CompanyFilterSidebar onCompanySelect={handleCompanySelect} selectedCompany={selectedCompany} data-id="element-1752" />
      </div>

      {/* Panel Principal */}
      <div className="flex-1 p-4 lg:p-6 overflow-y-auto" data-id="element-1753">
        {/* Cabecera y Botones */}
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-gray-800 mb-6" data-id="element-1754">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-4 mb-6" data-id="element-1755">
            <div data-id="element-1756">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-1757">
                Control de impresión de Etiquetas
              </h2>

              {!selectedCompany && <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-2" data-id="element-1758">
                  <AlertCircle className="text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" size={18} data-id="element-1759" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200" data-id="element-1760">
                    Selecciona una empresa del menú lateral para imprimir
                    etiquetas
                  </p>
                </div>}

              <div className="flex flex-wrap gap-3" data-id="element-1761">
                <Button onClick={handlePrintPending} variant="primary" leftIcon={<Printer className="w-4 h-4" data-id="element-1763" />} disabled={!selectedCompany || !showHistory && ordersToDisplay.filter(o => o.labelStatus === 'Pendiente').length === 0} data-id="element-1762">
                  {selectedOrderIds.size > 0 ? `Imprimir Seleccionadas (${selectedOrderIds.size})` : 'Imprimir Pendientes'}
                </Button>

                <Button onClick={handleReprintGenerated} variant="secondary" leftIcon={<RotateCcw className="w-4 h-4" data-id="element-1765" />} disabled={!selectedCompany || !showHistory || ordersToDisplay.filter(o => o.labelStatus === 'Generado').length === 0} className={!showHistory ? 'hidden' : ''} data-id="element-1764">
                  {selectedOrderIds.size > 0 ? `Reimprimir Seleccionadas (${selectedOrderIds.size})` : 'Reimprimir Generadas'}
                </Button>

                <Button onClick={handlePrintDeliveryNote} variant="outline" leftIcon={<PackageCheck className="w-4 h-4" data-id="element-1767" />} disabled={!selectedCompany || ordersToDisplay.length === 0} className="border-green-600 text-green-600 hover:bg-green-50 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-900/20" data-id="element-1766">
                  {selectedOrderIds.size > 0 ? `Conduce (${selectedOrderIds.size})` : 'Imprimir Conduce'}
                </Button>

                <Button variant={showHistory ? 'primary' : 'secondary'} leftIcon={<History className="w-4 h-4" data-id="element-1769" />} onClick={() => {
                setShowHistory(!showHistory);
                setSelectedOrderIds(new Set());
              }} data-id="element-1768">
                  {showHistory ? 'Ver Pendientes' : 'Ver Historial'}
                </Button>
              </div>
            </div>

            {/* Métricas */}
            <div className="flex flex-wrap gap-4 w-full lg:w-auto" data-id="element-1770">
              {metrics.map((metric, index) => <div key={index} onClick={metric.clickable ? metric.onClick : undefined} className={`bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex-1 lg:flex-none lg:w-40 ${metric.clickable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors' : ''}`} data-id="element-1771">
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium block mb-1" data-id="element-1772">
                    {metric.label}
                  </span>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1773">
                    {metric.value}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* Tabla de Datos */}
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-gray-800" data-id="element-1774">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-1775">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1776">
              {showHistory ? 'Historial de Etiquetas' : 'Etiquetas Pendientes'}
              {selectedCompany && ` - ${selectedCompany}`}
              {selectedOrderIds.size > 0 && <span className="ml-2 text-sm font-normal text-blue-600 dark:text-blue-400" data-id="element-1777">
                  ({selectedOrderIds.size} seleccionadas)
                </span>}
            </h3>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto" data-id="element-1778">
              {/* Filtro de Sucursal */}
              <FilterDropdown label="Sucursal" value={branchFilter} options={companyBranches} onChange={setBranchFilter} className="w-full sm:w-48" data-id="element-1779" />

              {/* Filtro de Tipo (Lunch/Dinner) */}
              <FilterDropdown label="Tipo" value={typeFilter} options={['Todo', 'Almuerzo', 'Cena']} onChange={setTypeFilter} className="w-full sm:w-40" data-id="element-1780" />

              {/* Buscador */}
              <SearchBar className="w-full sm:w-64" placeholder="Buscar por nombre, orden..." onSearch={setSearchTerm} data-id="element-1781" />
            </div>
          </div>

          {!selectedCompany ? <div className="text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-1782">
              <Printer size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-1783" />
              <p data-id="element-1784">Selecciona una empresa del menú lateral</p>
              <p className="text-sm mt-2" data-id="element-1785">Las etiquetas aparecerán aquí</p>
            </div> : ordersToDisplay.length === 0 ? <div className="text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-1786">
              <Printer size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-1787" />
              <p data-id="element-1788">
                No hay resultados para los filtros seleccionados
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1789">
              <table className="w-full text-left border-collapse" data-id="element-1790">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700" data-id="element-1791">
                  <tr data-id="element-1792">
                    <th className="px-4 py-3 w-10" data-id="element-1793">
                      <button onClick={toggleSelectAll} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-1794">
                        {selectedOrderIds.size > 0 && selectedOrderIds.size === ordersToDisplay.length ? <CheckSquare size={18} className="text-blue-600" data-id="element-1795" /> : <Square size={18} data-id="element-1796" />}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1797">
                      No. Orden
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1798">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1799">
                      Sucursal
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1800">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1801">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1802">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1803">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-right" data-id="element-1804">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1805">
                  {ordersToDisplay.map(order => <tr key={order.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer ${selectedOrderIds.has(order.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`} onClick={() => toggleOrderSelection(order.id)} data-id="element-1806">
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()} data-id="element-1807">
                        <button onClick={() => toggleOrderSelection(order.id)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" data-id="element-1808">
                          {selectedOrderIds.has(order.id) ? <CheckSquare size={18} className="text-blue-600" data-id="element-1809" /> : <Square size={18} data-id="element-1810" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white" data-id="element-1811">
                        {order.orderNo}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white" data-id="element-1812">
                        {order.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-1813">
                        {order.branch}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-1814">
                        {cleanProductName(order.product)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300" data-id="element-1815">
                        {order.catalogType}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400" data-id="element-1816">
                        {order.orderDate}
                      </td>
                      <td className="px-4 py-3" data-id="element-1817">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${order.labelStatus === 'Generado' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'}`} data-id="element-1818">
                          {order.labelStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()} data-id="element-1819">
                        {order.status === 'Listo' && <button onClick={() => handleMarkAsDelivered(order)} className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 text-sm font-medium flex items-center gap-1 ml-auto" data-id="element-1820">
                            <PackageCheck size={14} data-id="element-1821" />
                            Entregar
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>

      {showDeliveryConfirmModal && orderToDeliver && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-1822">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-1823">
            {/* Modal de confirmación */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1824">
              <div className="flex items-center gap-3" data-id="element-1825">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg" data-id="element-1826">
                  <PackageCheck className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-1827" />
                </div>
                <div data-id="element-1828">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1829">
                    Confirmar Entrega
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1830">
                    Orden #{orderToDeliver.orderNo}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6" data-id="element-1831">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4" data-id="element-1832">
                <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-2" data-id="element-1833">
                  ⚠️ Confirmación de Entrega
                </p>
                <p className="text-xs text-yellow-800 dark:text-yellow-200" data-id="element-1834">
                  Esta acción marcará el pedido como entregado y NO se puede
                  revertir.
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg" data-id="element-1835">
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1836">
                  <strong data-id="element-1837">Cliente:</strong> {orderToDeliver.name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1838">
                  <strong data-id="element-1839">Producto:</strong>{' '}
                  {cleanProductName(orderToDeliver.product)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-1840">
                  <strong data-id="element-1841">Empresa:</strong> {orderToDeliver.company}
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-800 flex gap-3" data-id="element-1842">
              <Button variant="outline" onClick={() => {
            setShowDeliveryConfirmModal(false);
            setOrderToDeliver(null);
          }} fullWidth data-id="element-1843">
                Cancelar
              </Button>
              <Button onClick={confirmDelivery} fullWidth className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800" data-id="element-1844">
                Confirmar Entrega
              </Button>
            </div>
          </div>
        </div>}

      {showTotalLabelsModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-1845">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl border border-gray-200 dark:border-gray-800 max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1846">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1847">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1848">
                Total de Etiquetas Activas
              </h2>
              <button onClick={() => setShowTotalLabelsModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-1849">
                <X size={20} data-id="element-1850" />
              </button>
            </div>
            {/* Contenido del modal */}
            <div className="p-0 overflow-y-auto flex-1" data-id="element-1851">
                {/* ... tabla resumen (si la tienes, insertarla aquí) ... */}
                <div className="p-6 text-center text-gray-500" data-id="element-1852">
                    Tabla de resumen de etiquetas activas
                </div>
            </div>
             <div className="p-6 border-t border-gray-200 dark:border-gray-800" data-id="element-1853">
              <Button onClick={() => setShowTotalLabelsModal(false)} fullWidth data-id="element-1854">
                Cerrar
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}